import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faExternalLinkAlt, faChevronRight, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams, useNavigate } from "react-router-dom";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/,"");
const PDF_URL = "/manuales/manual_admin.pdf";
const authFetch = async (url, token, opts={}) => {
  const res = await fetch(url, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: "no-store",
  });
  return res;
};
function normalizeBasic(s = "") {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
}
function buildTerms(q) { return normalizeBasic(q).split(" ").filter(Boolean); }
function pageMatches(textNorm, terms) { return terms.every((t) => textNorm.includes(t)); }
function makeSnippet(text, hitIndex, termLen, ctx = 90) {
  const start = Math.max(0, hitIndex - ctx);
  const end = Math.min(text.length, hitIndex + termLen + ctx);
  const raw = text.slice(start, end);
  return (start > 0 ? "… " : "") + raw + (end < text.length ? " …" : "");
}

const ManualAdmin = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [page, setPage] = useState(Number(params.get("page")) || 1);
  const [query, setQuery] = useState("");
  const [openSug, setOpenSug] = useState(false);
  const [fulltext, setFulltext] = useState({ pages: [] });
  const [results, setResults] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoadErr(null);
    (async () => {
      try {
        const url = `${API_BASE}/api/manuales/fulltext?role=ADMIN`;
        const resp = await authFetch(url, token);
        if (resp.status === 401) { setLoadErr("HTTP 401"); setFulltext({ pages: [] }); return; }
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const prepared = {
          pages: (data?.pages || []).map((p) => {
            const txt = (p?.text || "").replace(/\r/g, " ").replace(/\n/g, " ");
            return { page: Number(p?.page) || 1, text: txt, _norm: normalizeBasic(txt) };
          }),
        };
        if (alive) setFulltext(prepared);
      } catch (e) {
        console.error("[ManualAdmin] Índice:", e);
        if (alive) { setLoadErr(String(e?.message || e)); setFulltext({ pages: [] }); }
      }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    const url = `${PDF_URL}#page=${page}&zoom=page-width`;
    if (iframeRef.current) iframeRef.current.src = url;
  }, [page]);

  useEffect(() => {
    const p = Number(params.get("page"));
    if (p && p !== page) setPage(p);
  }, [params]);

  useEffect(() => {
    const q = (query || "").trim();
    if (!q || !fulltext.pages.length) { setResults([]); return; }
    const terms = buildTerms(q);
    const out = [];
    for (const p of fulltext.pages) {
      if (!p._norm) continue;
      if (pageMatches(p._norm, terms)) {
        const first = terms[0];
        const hitIndex = p._norm.indexOf(first);
        const snippet = makeSnippet(p.text, Math.max(0, hitIndex), first.length, 90);
        out.push({ page: p.page, title: `Coincidencia en página ${p.page}`, snippet });
        if (out.length >= 50) break;
      }
    }
    setResults(out);
  }, [query, fulltext]);

  const jump = (p) => {
    const n = Math.max(1, Number(p) || 1);
    setPage(n);
    navigate(`/manual/admin?page=${n}`, { replace: true });
  };

  const openNew = () => window.open(`${PDF_URL}#page=${page}&zoom=page-width`, "_blank", "noopener");

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] mt-16">
      <div className="px-4 py-3 border-b bg-white flex items-center gap-3">
        <h1 className="text-xl font-semibold">Manual del Administrador</h1>

        <div className="relative ml-auto w-full max-w-xl">
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2 shadow-sm">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
            <input
              className="flex-1 outline-none"
              placeholder="Buscar en todo el manual…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpenSug(true); }}
              onFocus={() => setOpenSug(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { if (results.length) jump(results[0].page); setOpenSug(false); }
                else if (e.key === "Escape") setOpenSug(false);
              }}
            />
            <button className="text-sm px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => { if (results.length) jump(results[0].page); setOpenSug(false); }}>
              Ir
            </button>
            <button className="text-sm px-2 py-1 rounded border hover:bg-gray-50" onClick={openNew} title="Abrir en pestaña nueva">
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </button>
          </div>

          {openSug && (
            <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-80 overflow-auto"
                 onMouseLeave={() => setOpenSug(false)}>
              {loadErr && <div className="text-sm text-red-600 px-3 py-2">No se pudo cargar el índice ({loadErr}).</div>}
              {!loadErr && query && results.length === 0 && <div className="text-sm text-gray-600 px-3 py-2">Sin resultados.</div>}
              {results.map((it, i) => (
                <div key={`${it.page}-${i}`} className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                     onClick={() => { jump(it.page); setOpenSug(false); }}>
                  <div className="text-sm font-medium">{it.title}</div>
                  <div className="text-xs text-gray-600 mt-1">{it.snippet}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-72 border-r overflow-auto hidden md:block">
          <div className="p-3">
            <div className="text-xs uppercase text-gray-500 mb-2">Navegación</div>
            <div className="mt-2 flex items-center gap-2">
              <button className="px-2 py-1 rounded border hover:bg-gray-50" onClick={() => jump(Math.max(1, page - 1))} title="Página anterior">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <input className="w-16 border rounded px-2 py-1" type="number" min={1} value={page} onChange={(e) => jump(e.target.value)} />
              <button className="px-2 py-1 rounded border hover:bg-gray-50" onClick={() => jump(page + 1)} title="Página siguiente">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 bg-gray-100">
          <iframe ref={iframeRef} title="Manual del Administrador" src={`${PDF_URL}#page=${page}&zoom=page-width`} className="w-full h-full" />
        </main>
      </div>
    </div>
  );
};

export default ManualAdmin;
